


import React, { useCallback, useEffect, useState } from 'react';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { GetQuizApi } from '../redux/actions/teams/members/GetQuizAction';
import { GetCategoryApi } from '../redux/actions/teams/members/GetCategoryAction';
import { PulseLoader } from 'react-spinners';
import axiosInstance from '../api/axiosInstance';
import { endpoints } from '../services/endpoints';
import { showErrorToast, showSuccessToast } from '../helpers/toast';

const Quiz = () => {
    const columns = ['Quiz Name', 'Quiz Category','Created On','Status','Action'];
    const { response: listResponse, loading: listLoading, error: listError } = useSelector((state) => state.quizList);
    const { response: categoryListResponse, loading: categoryListLoading, error: categoryListError } = useSelector((state) => state.categoryList);
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [limit, setLimit] = useState(5);
    const [dataList, setDataList] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [isToggled, setIsToggled] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [excelFile, setExcelFile] = useState(null);
    const [excelPreview, setExcelPreview] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);



    const fetchQuizList = useCallback((currentPage, limit, searchTerm, categoryId) => {
        dispatch(GetQuizApi(currentPage, limit, searchTerm, categoryId));
    }, [dispatch]);

    const fetchCategories = useCallback(() => {
    dispatch(GetCategoryApi());
}, [dispatch]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };

    const handleSearchChange = (term) => {
        setSearchTerm(term);
        if (searchTerm !== term) {
            setCurrentPage(1); 
        }
    };


    const handleRetry = () => {
  window.location.reload(); // or trigger your data-fetching again
};

    const updatePreviewQuiz = (quizIndex, field, value) => {
        setExcelPreview((prev) => {
            if (!prev) return prev;
            const quizzes = [...(prev.quizzes || [])];
            quizzes[quizIndex] = { ...quizzes[quizIndex], [field]: value };
            if (field === 'quiz_category') {
                quizzes[quizIndex].category_name = categoryListResponse?.data?.find((category) => category._id === value)?.name || '';
            }
            return { ...prev, quizzes };
        });
    };

    const updatePreviewQuestion = (quizIndex, questionIndex, field, value) => {
        setExcelPreview((prev) => {
            if (!prev) return prev;
            const quizzes = [...(prev.quizzes || [])];
            const quiz = { ...quizzes[quizIndex] };
            const questions = [...(quiz.questions || [])];
            const question = { ...questions[questionIndex] };

            if (field.startsWith('options.')) {
                const optionKey = field.split('.')[1];
                question.options = { ...(question.options || {}), [optionKey]: value };
            } else if (field.startsWith('true_false.')) {
                const optionKey = field.split('.')[1];
                question.true_false = { ...(question.true_false || {}), [optionKey]: value };
            } else {
                question[field] = value;
                if (field === 'question_type') {
                    question.correct_answer = '';
                    if (value === 'mcq') {
                        question.options = question.options || { a: '', b: '', c: '', d: '' };
                        delete question.true_false;
                    } else {
                        question.true_false = question.true_false || { a: 'True', b: 'False' };
                        delete question.options;
                    }
                }
            }

            questions[questionIndex] = question;
            quiz.questions = questions;
            quiz.total_questions = questions.length;
            quizzes[quizIndex] = quiz;
            return {
                ...prev,
                quizzes,
                totalQuestions: quizzes.reduce((sum, item) => sum + (item.questions?.length || 0), 0),
            };
        });
    };

    const buildExcelFormData = () => {
        const formData = new FormData();
        formData.append('quiz_excel', excelFile);
        return formData;
    };

    const handleExcelPreview = async () => {
        if (!excelFile) {
            showErrorToast('Please select an Excel file');
            return;
        }

        try {
            setExcelLoading(true);
            const response = await axiosInstance.post(endpoints.previewQuizExcel, buildExcelFormData(), {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setExcelPreview(response.data?.data || null);
            setShowPreviewModal(true);
        } catch (error) {
            showErrorToast(error?.response?.data?.message || 'Failed to preview quiz Excel');
        } finally {
            setExcelLoading(false);
        }
    };

    const handleExcelImport = async () => {
        if (!excelPreview?.quizzes?.length || excelPreview?.errors?.length) return;

        try {
            setExcelLoading(true);
            const response = await axiosInstance.post(endpoints.uploadQuizExcel, {
                quizzes: excelPreview.quizzes,
            });
            showSuccessToast(response.data?.message || 'Quizzes uploaded successfully');
            setExcelFile(null);
            setExcelPreview(null);
            setShowPreviewModal(false);
            setShowUpload(false);
            fetchQuizList(1, limit, searchTerm, selectedCategory);
            setCurrentPage(1);
        } catch (error) {
            showErrorToast(error?.response?.data?.message || 'Failed to upload quiz Excel');
        } finally {
            setExcelLoading(false);
        }
    };

    useEffect(() => {
    if (isToggled) {
        fetchQuizList(currentPage, limit, searchTerm, selectedCategory);
        setIsToggled(false);
    }
}, [isToggled, fetchQuizList, currentPage, limit, searchTerm, selectedCategory]);

    useEffect(() => {
        if (location.pathname === '/dashboard/quiz-management') {
            setCurrentPage(1);
            setLimit(5);
            setSearchTerm('');
            setSelectedCategory('');
        }
    }, [location]);

    useEffect(() => {
    fetchCategories();
}, [fetchCategories]);

    useEffect(() => {
        fetchQuizList(currentPage, limit, searchTerm, selectedCategory);
    }, [currentPage, limit, searchTerm, selectedCategory,fetchQuizList]);

    useEffect(() => {
        if (listResponse?.status) {
            setCurrentPage(listResponse?.data?.currentPage);
            setTotalPages(listResponse?.data?.totalPages);
            setTotalItems(listResponse?.data?.totalItems);
            setDataList(listResponse?.data?.result || []);
        }
    }, [listResponse]);

    return (
        <div className="content-wrapper">
            <section className="main-section spacer-y">
                <div className="container">
                    <div className="row gy-4">
                        <div className="col-12">
                           <div className="common_sub_heading_wrapper d-flex justify-content-between align-items-center mb-3">
                                <h2>Quiz</h2>
                                
                                <div className="d-flex gap-2 align-items-center">
                                    {/* Category Dropdown */}
                                    <select
                                        className="form-select"
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                        style={{ width: 'auto' }}
                                        disabled={categoryListLoading}
                                    >
                                        <option value="">Select Category</option>
                                        {categoryListResponse?.data?.map((category) => (
                                            <option key={category._id} value={category._id}>
                                                {category.name}
                                            </option>
                                        ))}
                                        {categoryListError && (
                                            <option value="" disabled>Error loading categories</option>
                                        )}
                                    </select>

                                    {/* Add Quiz Button */}
                                    <button
                                        className="btn btn-outline-success"
                                        onClick={() => setShowUpload((prev) => !prev)}
                                    >
                                        Upload Quiz
                                    </button>

                                    <button
                                        className="btn btn-success"
                                        onClick={() => navigate('/dashboard/add-quiz')}
                                    >
                                        + Add Quiz
                                    </button>
                                </div>
                            </div>

                            {showUpload && (
                                <div className="bg-white border rounded p-3 mb-3">
                                    <div className="row g-3 align-items-end">
                                        <div className="col-12 col-md-7">
                                            <h6>Upload Quiz Excel</h6>
                                            <input
                                                type="file"
                                                className="form-control"
                                                accept=".xlsx,.xls"
                                                onChange={(e) => {
                                                    setExcelFile(e.target.files?.[0] || null);
                                                    setExcelPreview(null);
                                                }}
                                            />
                                            <small className="text-muted">
                                                Columns: quiz_name, category_id, question, question_type, option_a-d, correct_answer, image_url.
                                            </small>
                                        </div>
                                        <div className="col-12 col-md-5 d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={handleExcelPreview}
                                                disabled={excelLoading}
                                            >
                                                Preview
                                            </button>
                                        </div>
                                    </div>

                                    {excelLoading && (
                                        <div className="text-center my-3">
                                            <PulseLoader size={10} color="#49a496" />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Keep the search input where it was */}
                           <div className="mb-3 position-relative" style={{ maxWidth: '300px' }}>
                            <input
                                type="text"
                                className="form-control ps-4"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                            <span className="position-absolute top-50 start-0 translate-middle-y ms-2">
                                <i className="fas fa-search text-muted"></i>
                            </span>
                        </div>

                            <div className="tab-content common_tab_content" id="pills-tabContent">
                                <div
                                    className="tab-pane fade show active"
                                    id="pills-1"
                                    role="tabpanel"
                                    aria-labelledby="pills-1-tab"
                                    tabIndex="0"
                                >
                                    {listLoading ? (
                                        <div style={{ height: '40vh' }} className="d-flex align-items-center justify-content-center">
                                            <PulseLoader size={25} color="#49a496" />
                                        </div>
                                    ) : listError ? (
                                     
                                        <div className="center-container">
                                            <div className="error-box">
                                            <i className="bi bi-exclamation-triangle-fill"></i>
                                            <p>Something went wrong</p>
                                            <button className="retry-btn" onClick={handleRetry}>
                                                Try Again
                                            </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <Table
                                                tableData={dataList}
                                                setDataList={setDataList}
                                                limit={limit}
                                                columns={columns}
                                                currentPage={currentPage}
                                                handlePageChange={handlePageChange}
                                                setLimit={handleLimitChange}
                                                title={'quiz'}
                                                setIsToggled={setIsToggled}

                                            />
                                            {dataList?.length > 0 && (
                                                <Pagination
                                                    currentPage={currentPage}
                                                    totalPages={totalPages}
                                                    limit={limit}
                                                    totalItems={totalItems}
                                                    handlePageChange={handlePageChange}
                                                    handleLimitChange={handleLimitChange}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {showPreviewModal && excelPreview && (
                <div
                    className="modal fade show"
                    style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.45)' }}
                    tabIndex="-1"
                    role="dialog"
                >
                    <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <div>
                                    <h5 className="modal-title">Quiz Preview</h5>
                                    <div className="d-flex gap-3 flex-wrap mt-1">
                                        <small>{excelPreview.totalQuizzes || 0} quizzes</small>
                                        <small>{excelPreview.totalQuestions || 0} questions</small>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowPreviewModal(false)}
                                    aria-label="Close"
                                />
                            </div>
                            <div className="modal-body">
                                {excelPreview.errors?.length > 0 && (
                                    <div className="alert alert-danger">
                                        {excelPreview.errors.slice(0, 8).map((item, index) => (
                                            <div key={index}>{item}</div>
                                        ))}
                                        {excelPreview.errors.length > 8 && (
                                            <div>+ {excelPreview.errors.length - 8} more errors</div>
                                        )}
                                    </div>
                                )}

                                {excelPreview.quizzes?.length > 0 && (
                                    <div className="d-flex flex-column gap-3">
                                        {excelPreview.quizzes.map((quiz, quizIndex) => (
                                            <div className="border rounded p-3" key={`${quiz.quiz_name}-${quizIndex}`}>
                                                <div className="row g-2">
                                                    <div className="col-12 col-md-6">
                                                        <h6>Quiz Name</h6>
                                                        <input
                                                            className="form-control"
                                                            value={quiz.quiz_name || ''}
                                                            onChange={(e) => updatePreviewQuiz(quizIndex, 'quiz_name', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                        <h6>Category</h6>
                                                        <select
                                                            className="form-select"
                                                            value={quiz.quiz_category || ''}
                                                            onChange={(e) => updatePreviewQuiz(quizIndex, 'quiz_category', e.target.value)}
                                                        >
                                                            <option value="">Select Category</option>
                                                            {categoryListResponse?.data?.map((category) => (
                                                                <option key={category._id} value={category._id}>{category.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="col-12">
                                                        <h6>Image URL</h6>
                                                        <input
                                                            className="form-control"
                                                            value={quiz.quiz_image || ''}
                                                            onChange={(e) => updatePreviewQuiz(quizIndex, 'quiz_image', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-12">
                                                        <h6>Description</h6>
                                                        <textarea
                                                            className="form-control"
                                                            value={quiz.description || ''}
                                                            onChange={(e) => updatePreviewQuiz(quizIndex, 'description', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-12 col-md-4">
                                                        <h6>Total Marks</h6>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={quiz.total_marks || 0}
                                                            onChange={(e) => updatePreviewQuiz(quizIndex, 'total_marks', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-12 col-md-4">
                                                        <h6>Total Time</h6>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={quiz.total_time || 1}
                                                            onChange={(e) => updatePreviewQuiz(quizIndex, 'total_time', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-12 col-md-4">
                                                        <h6>Status</h6>
                                                        <select
                                                            className="form-select"
                                                            value={quiz.statusss ?? 1}
                                                            onChange={(e) => updatePreviewQuiz(quizIndex, 'statusss', Number(e.target.value))}
                                                        >
                                                            <option value={1}>Active</option>
                                                            <option value={0}>Inactive</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-12">
                                                        <h6>Tips</h6>
                                                        <textarea
                                                            className="form-control"
                                                            value={Array.isArray(quiz.tips) ? quiz.tips.join('\n') : quiz.tips || ''}
                                                            onChange={(e) => updatePreviewQuiz(quizIndex, 'tips', e.target.value.split('\n').filter((item) => item.trim()))}
                                                        />
                                                    </div>
                                                    <div className="col-12">
                                                        <h6>NHS Guidance</h6>
                                                        {(quiz.nhs_guidance || []).length ? (
                                                            <div className="table-responsive">
                                                                <table className="table table-sm table-bordered">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Text</th>
                                                                            <th>URL</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(quiz.nhs_guidance || []).map((item, index) => (
                                                                            <tr key={index}>
                                                                                <td>{item.text}</td>
                                                                                <td className="text-break">{item.url}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        ) : (
                                                            <p className="text-muted mb-0">No guidance links</p>
                                                        )}
                                                    </div>
                                                </div>

                                                <h6 className="mt-3">Questions</h6>
                                                <div className="d-flex flex-column gap-2">
                                                    {(quiz.questions || []).map((question, questionIndex) => {
                                                        const isMCQ = question.question_type !== 'true_false';
                                                        return (
                                                            <div className="border rounded p-2" key={questionIndex}>
                                                                <div className="row g-2">
                                                                    <div className="col-12 col-md-8">
                                                                        <input
                                                                            className="form-control"
                                                                            value={question.question || ''}
                                                                            onChange={(e) => updatePreviewQuestion(quizIndex, questionIndex, 'question', e.target.value)}
                                                                        />
                                                                    </div>
                                                                    <div className="col-12 col-md-4">
                                                                        <select
                                                                            className="form-select"
                                                                            value={question.question_type || 'mcq'}
                                                                            onChange={(e) => updatePreviewQuestion(quizIndex, questionIndex, 'question_type', e.target.value)}
                                                                        >
                                                                            <option value="mcq">MCQ</option>
                                                                            <option value="true_false">True/False</option>
                                                                        </select>
                                                                    </div>
                                                                    {isMCQ ? ['a', 'b', 'c', 'd'].map((option) => (
                                                                        <div className="col-12 col-md-6" key={option}>
                                                                            <input
                                                                                className="form-control"
                                                                                placeholder={`Option ${option.toUpperCase()}`}
                                                                                value={question.options?.[option] || ''}
                                                                                onChange={(e) => updatePreviewQuestion(quizIndex, questionIndex, `options.${option}`, e.target.value)}
                                                                            />
                                                                        </div>
                                                                    )) : ['a', 'b'].map((option) => (
                                                                        <div className="col-12 col-md-6" key={option}>
                                                                            <input
                                                                                className="form-control"
                                                                                placeholder={option === 'a' ? 'True text' : 'False text'}
                                                                                value={question.true_false?.[option] || question.options?.[option] || ''}
                                                                                onChange={(e) => updatePreviewQuestion(quizIndex, questionIndex, `true_false.${option}`, e.target.value)}
                                                                            />
                                                                        </div>
                                                                    ))}
                                                                    <div className="col-12 col-md-4">
                                                                        <select
                                                                            className="form-select"
                                                                            value={question.correct_answer || ''}
                                                                            onChange={(e) => updatePreviewQuestion(quizIndex, questionIndex, 'correct_answer', e.target.value)}
                                                                        >
                                                                            <option value="">Correct Answer</option>
                                                                            {(isMCQ ? ['a', 'b', 'c', 'd'] : ['a', 'b']).map((option) => (
                                                                                <option key={option} value={option}>{option.toUpperCase()}</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                    <div className="col-12 col-md-8">
                                                                        <input
                                                                            className="form-control"
                                                                            placeholder="Explanation"
                                                                            value={question.explanation || ''}
                                                                            onChange={(e) => updatePreviewQuestion(quizIndex, questionIndex, 'explanation', e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowPreviewModal(false)}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleExcelImport}
                                    disabled={excelLoading || excelPreview?.errors?.length > 0}
                                >
                                    Save Preview
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Quiz;
